import { FC, ReactNode, useMemo } from "react";
import { Stack, Tooltip, Typography } from "@mui/material";
import { formatCompactNumber } from "../../../common/utils";
import { ChartNoDataOverlay } from "../components/ChartNoDataOverlay";
import { GraphBarChartLegend } from "../components/GraphBarChartLegend";

export type HorizontalBarItem = {
	id: string | number;
	value: number;
	icon: ReactNode;
	tooltip?: ReactNode;
};

type Props = {
	data: HorizontalBarItem[];
	increaseLabel?: string;
	decreaseLabel?: string;
	height?: number;
	numberOfTicks?: number;
	extraAxisValue?: number;
	formatTick?: (value: number) => string;
	showLegend?: boolean;
};

// Local constants
const iconWidth = 32;
const rowSpacing = 8;
const chartPaddingX = 2;
const chartPaddingPx = chartPaddingX * 8;

const HorizontalBarChart: FC<Props> = ({
	data,
	increaseLabel = "Increase",
	decreaseLabel = "Decrease",
	height = 220,
	numberOfTicks = 5,
	extraAxisValue = 1_000_000,
	formatTick = (value) => formatCompactNumber(value, 0, 1),
	showLegend = true,
}) => {
	// Find the biggest absolute value in the dataset
	const maxAbsDataValue = useMemo(() => Math.max(...data.map((item) => Math.abs(item.value)), 0), [data]);

	// Add extra space to the axis so the largest bar does not touch the edge
	const axisMaxValue = useMemo(
		() => Math.max(maxAbsDataValue + extraAxisValue, 1),
		[maxAbsDataValue, extraAxisValue]
	);

	// Create the X axis ticks from negative max to positive max
	const ticks = useMemo(
		() =>
			Array.from({ length: numberOfTicks }, (_, index) => {
				// If only one tick is requested, show zero
				if (numberOfTicks === 1) return 0;

				// Spread ticks evenly between -axisMaxValue and +axisMaxValue
				return -axisMaxValue + (index * axisMaxValue * 2) / (numberOfTicks - 1);
			}),
		[axisMaxValue, numberOfTicks]
	);

	const hasNoData = data?.length === 0;

	const graphLeftSpace = hasNoData ? 0 : iconWidth;

	return (
		<Stack spacing={2} width="100%" minHeight={height}>
			<Stack spacing={1.5} flex={1} justifyContent="center" position="relative">
				{/* Continuous vertical grid lines behind all bars */}
				<Stack
					position="absolute"
					top={0}
					bottom={0}
					left={`calc(${graphLeftSpace}px + ${rowSpacing}px + ${chartPaddingPx}px)`}
					right={`${chartPaddingPx}px`}
				>
					{ticks.map((tick, index) => {
						// Convert the tick value into a percentage position on the full axis
						const left = ((tick + axisMaxValue) / (axisMaxValue * 2)) * 100;

						return (
							<Stack
								key={`grid-${tick}-${index}`}
								position="absolute"
								left={`${left}%`}
								top={0}
								bottom={0}
								width="1px"
								bgcolor="divider"
								sx={{
									transform: "translateX(-50%)",
									opacity: 0.75,
								}}
							/>
						);
					})}
				</Stack>

				{hasNoData && <ChartNoDataOverlay />}

				{!hasNoData &&
					data.map((item) => {
						// Positive values grow to the right
						const isPositive = item.value > 0;

						// Negative values grow to the left
						const isNegative = item.value < 0;

						// Convert the item value into a ratio against the axis max
						const ratio = Math.abs(item.value) / axisMaxValue;

						// The full axis is 100%, so one side of zero is 50%
						const barWidth = `${ratio * 50}%`;

						return (
							<Tooltip key={item.id} title={item.tooltip ?? item.value} arrow>
								<Stack
									direction="row"
									alignItems="center"
									spacing={1}
									width="100%"
									position="relative"
									py={0.4}
								>
									{/* Y axis icon */}
									<Stack width={iconWidth} direction="row" justifyContent="center">
										{item.icon}
									</Stack>

									{/* Chart row area */}
									<Stack flex={1} px={chartPaddingX}>
										<Stack position="relative" height={18}>
											{/* Negative bar */}
											{isNegative && (
												<Stack
													position="absolute"
													right="50%"
													width={barWidth}
													height="100%"
													borderRadius={1}
													bgcolor="secondary.main"
												/>
											)}

											{/* Positive bar */}
											{isPositive && (
												<Stack
													position="absolute"
													left="50%"
													width={barWidth}
													height="100%"
													borderRadius={1}
													bgcolor="primary.main"
												/>
											)}
										</Stack>
									</Stack>
								</Stack>
							</Tooltip>
						);
					})}
			</Stack>

			{/* X axis labels */}
			<Stack direction="row" spacing={1} alignItems="center">
				{/* Empty space to align X axis with the bar area after icons */}
				<Stack width={iconWidth} />

				<Stack flex={1} px={chartPaddingX}>
					<Stack position="relative" height={22}>
						{ticks.map((tick, index) => {
							// Convert the tick value into a percentage position on the full axis
							const left = ((tick + axisMaxValue) / (axisMaxValue * 2)) * 100;

							return (
								<Stack
									key={`tick-${tick}-${index}`}
									position="absolute"
									left={`${left}%`}
									sx={{ transform: "translateX(-50%)" }}
								>
									<Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
										{formatTick(tick)}
									</Typography>
								</Stack>
							);
						})}
					</Stack>
				</Stack>
			</Stack>

			{/* Legend */}
			{showLegend && !hasNoData && (
				<GraphBarChartLegend title="tkm %" primaryLable={increaseLabel} secondaryLable={decreaseLabel} />
			)}
		</Stack>
	);
};

export default HorizontalBarChart;
